import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { logInfo } from '../services/logtail';
import {
  DESIGN_STORAGE_KEY,
  captureDesignFromUrl,
  parseDesignParam,
} from '../utils/utm';

export type DesignMode = 'sketchy' | 'classic';

interface DesignContextValue {
  design: DesignMode;
  setDesign: (design: DesignMode) => void;
  isSketchy: boolean;
}

const DesignContext = createContext<DesignContextValue | undefined>(undefined);

export { DESIGN_STORAGE_KEY };

export const DesignProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [design, setDesignState] = useState<DesignMode>(() => {
    const fromUrl = parseDesignParam(new URLSearchParams(window.location.search));
    if (fromUrl) return fromUrl;
    const stored = window.localStorage.getItem(DESIGN_STORAGE_KEY) as DesignMode | null;
    if (stored === 'sketchy' || stored === 'classic') return stored;
    return 'sketchy';
  });

  useEffect(() => {
    const fromUrl = captureDesignFromUrl();
    if (fromUrl && fromUrl !== design) {
      setDesignState(fromUrl);
    }
  }, []);

  const setDesign = (next: DesignMode) => {
    setDesignState(next);
    window.localStorage.setItem(DESIGN_STORAGE_KEY, next);
  };

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.toggle('sketchy-redesign', design === 'sketchy');
    logInfo('Design mode updated', { design });
  }, [design]);

  const value = useMemo<DesignContextValue>(
    () => ({ design, setDesign, isSketchy: design === 'sketchy' }),
    [design]
  );

  return <DesignContext.Provider value={value}>{children}</DesignContext.Provider>;
};

export const useDesignMode = (): DesignContextValue => {
  const ctx = useContext(DesignContext);
  if (!ctx) throw new Error('useDesignMode must be used within DesignProvider');
  return ctx;
};
